import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import FmsInfoField from '#/modules/finMgmt/shared/components/FmsInfoField';
import { formatDateTimeOrDash } from '#/utils/date';
import { formatFmsAmount } from '#/modules/finMgmt/shared/helper/fmsAmount';

import { formatRoundingModeLabel } from '../helper/paymentRequestStatus';
import PaymentRequestStatusBadge from './PaymentRequestStatusBadge';

const TEXT_SPAN = 'sm:col-span-2 md:col-span-2';

function displayOrDash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

function formatPayeeLabel(payee) {
  if (!payee) {
    return '—';
  }
  const name = payee.name || '';
  const accountNo = payee.accountNo || '';
  if (name && accountNo) {
    return `${name}（${accountNo}）`;
  }
  return name || accountNo || '—';
}

/** 最近一次退回／取消原因（history 由舊到新或新到舊皆可，取 changedAt 最大者）。 */
function pickLatestReason(history, toStatus) {
  const matched = (history ?? [])
    .filter((item) => item?.toStatus === toStatus && item?.reason)
    .sort((a, b) => String(b.changedAt ?? '').localeCompare(String(a.changedAt ?? '')));
  return matched[0]?.reason ?? '';
}

/** 請款單 detail 上方資訊卡：hero（單號／狀態／領款人／操作）＋ 欄位格。 */
const PaymentRequestMainInfoCard = ({ detail, actions = null }) => {
  const rejectReason = detail?.status === 'RETURNED'
    ? pickLatestReason(detail?.history, 'RETURNED')
    : '';
  const cancelReason = detail?.status === 'CANCELLED'
    ? pickLatestReason(detail?.history, 'CANCELLED')
    : '';

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">
                {detail?.requestNo || '未編號草稿'}
              </h1>
              {detail?.status ? <PaymentRequestStatusBadge status={detail.status} /> : null}
            </div>
            <p className="mt-1 text-sm text-gray-600">
              領款人：
              {formatPayeeLabel(detail?.payee)}
            </p>
          </div>
          {actions ? (
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              {actions}
            </div>
          ) : null}
        </div>

        <div
          className={cn(
            'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
            'sm:grid-cols-2 md:grid-cols-4',
          )}
        >
          <FmsInfoField label="建立" value={formatDateTimeOrDash(detail?.record?.createdAt)} />
          <FmsInfoField label="修改" value={formatDateTimeOrDash(detail?.record?.updatedAt)} />
          <FmsInfoField label="請款總額" value={formatFmsAmount(detail?.totalAmount)} />
          <FmsInfoField label="取整方式" value={formatRoundingModeLabel(detail?.roundingMode)} />

          <FmsInfoField
            label="請款事由"
            value={displayOrDash(detail?.paymentReason)}
            className={TEXT_SPAN}
          />
          <FmsInfoField label="備註" value={displayOrDash(detail?.remark)} className={TEXT_SPAN} />

          {rejectReason ? (
            <FmsInfoField label="退回原因" value={rejectReason} className={TEXT_SPAN} />
          ) : null}
          {cancelReason ? (
            <FmsInfoField label="取消原因" value={cancelReason} className={TEXT_SPAN} />
          ) : null}
        </div>
      </div>
    </div>
  );
};

PaymentRequestMainInfoCard.propTypes = {
  detail: PropTypes.object.isRequired,
  actions: PropTypes.node,
};

export default PaymentRequestMainInfoCard;
